/**
 * Tools Page
 */

import { Star, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ToolRegistry } from '@/engine/ToolRegistry';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { ITool } from '@/interfaces/ITool';
import { setStorageItem } from '@/lib/storageUtils';
import { useAppStore } from '@/store/useAppStore';

type ToolTag = 'All' | 'Encoder' | 'Decoder' | 'Generator' | 'Formatter' | 'Text' | 'Crypto';

type ToolGroupMode = 'input' | 'category' | 'keyword';

function matchesTag(tool: ITool, tag: ToolTag): boolean {
  if (tag === 'All') return true;

  const name = tool.name.toLowerCase();
  const id = tool.id.toLowerCase();
  const category = (tool.category ?? '').toLowerCase();

  switch (tag) {
    case 'Encoder':
      return id.includes('encode') || name.includes('encode') || category === 'encoding';
    case 'Decoder':
      return id.includes('decode') || name.includes('decode') || category === 'encoding' || category === 'jwt';
    case 'Generator':
      return tool.type === 'generator' || id.includes('generator');
    case 'Formatter':
      return name.includes('format') || name.includes('minify') || name.includes('validate') || category === 'json';
    case 'Text':
      return category === 'text';
    case 'Crypto':
      return category === 'hash' || category === 'jwt';
    default:
      return true;
  }
}

function toolMatchesSearch(tool: ITool, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const haystack = [tool.name, tool.description, tool.category, ...(tool.keywords ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function ToolCard({
  tool,
  isFavorite,
  onOpen,
  onToggleFavorite,
  onAddToPipeline,
}: {
  tool: ITool;
  isFavorite: boolean;
  onOpen: () => void;
  onToggleFavorite: () => void;
  onAddToPipeline: () => void;
}) {
  const category = (tool.category ?? '').trim();

  return (
    <div className="relative">
      <button
        onClick={onOpen}
        className="w-full text-left border border-border rounded-none p-4 bg-muted/10 hover:bg-muted/30 transition-colors"
        title={tool.id}
      >
        <div className="pr-10">
          <div className="font-medium leading-5">{tool.name}</div>
          <div className="text-sm text-muted-foreground mt-1 line-clamp-3">
            {tool.description}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="min-w-0">
            {category ? (
              <span className="inline-flex items-center px-2 py-0.5 text-xs border border-border bg-background text-muted-foreground rounded-full">
                {category}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">&nbsp;</span>
            )}
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAddToPipeline();
                }}
                aria-label="Add to pipeline"
              >
                +
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Add to pipeline</TooltipContent>
          </Tooltip>
        </div>
      </button>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-3 right-3 h-8 w-8 px-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFavorite();
            }}
            aria-label={isFavorite ? 'Unfavorite' : 'Favorite'}
          >
            <Star
              className={'h-4 w-4 ' + (isFavorite ? 'text-primary fill-current' : 'text-muted-foreground')}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{isFavorite ? 'Unfavorite' : 'Favorite'}</TooltipContent>
      </Tooltip>
    </div>
  );
}

export const ToolsPage: React.FC<{ onNavigate: (to: string) => void }> = ({
  onNavigate,
}) => {
  const { addTool } = useAppStore();
  const [favoriteToolIds, setFavoriteToolIds] = useLocalStorage<string[]>('app.favoriteToolIds', [], {
    legacyKeys: ['test.favoriteToolIds'],
  });
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState<ToolTag>('All');
  const [groupMode, setGroupMode] = useState<ToolGroupMode>('input');

  const tags: ToolTag[] = ['All', 'Encoder', 'Decoder', 'Generator', 'Formatter', 'Text', 'Crypto'];

  const allTools = useMemo(() => ToolRegistry.getAll(), []);

  const filtered = useMemo(() => {
    const tools = allTools
      .filter((t) => matchesTag(t, activeTag))
      .filter((t) => toolMatchesSearch(t, query))
      .sort((a, b) => a.name.localeCompare(b.name));
    return tools;
  }, [activeTag, allTools, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, ITool[]>();

    const add = (group: string, tool: ITool) => {
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(tool);
    };

    const groupsForTool = (tool: ITool): string[] => {
      if (groupMode === 'category') return [tool.category || 'Other'];

      if (groupMode === 'keyword') {
        const keywords = (tool.keywords || [])
          .map((k) => String(k).trim())
          .filter(Boolean)
          .map((k) => k.toLowerCase());
        return keywords.length ? keywords : ['unkeyworded'];
      }

      // groupMode === 'input'
      if (!tool.supportedInputTypes || tool.supportedInputTypes.length === 0) {
        return ['No Input'];
      }
      return tool.supportedInputTypes.map((t) => t.semanticType);
    };

    for (const tool of filtered) {
      for (const g of groupsForTool(tool)) add(g, tool);
    }

    return new Map(Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b)));
  }, [filtered, groupMode]);

  const handleUseTool = (toolId: string) => {
    setStorageItem('app.selectedToolId', toolId);
    onNavigate('/editor');
  };

  const toggleFavorite = (toolId: string) => {
    setFavoriteToolIds((prev) => {
      if (prev.includes(toolId)) return prev.filter((id) => id !== toolId);
      return [...prev, toolId];
    });
  };

  return (
    <div className="h-full overflow-auto">
      <TooltipProvider>
      <div className="max-w-6xl mx-auto p-8">
        <h1 className="text-3xl font-semibold">Tool Browser</h1>

        <div className="mt-6">
          <div className="relative">
            <Input
              placeholder="Search for a tool..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={query ? 'pr-9' : undefined}
            />
            {query ? (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
                onClick={() => setQuery('')}
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-4">
          <div className="text-xs text-muted-foreground mb-2">Filter by tags</div>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <Button
                key={t}
                variant={activeTag === t ? 'secondary' : 'ghost'}
                size="sm"
                className="rounded-full"
                onClick={() => setActiveTag(t)}
              >
                {t}
              </Button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
          <div className="sm:col-span-2" />
          <div>
            <div className="text-xs text-muted-foreground mb-2">Group by</div>
            <Select
              value={groupMode}
              onValueChange={(val) => setGroupMode(val as ToolGroupMode)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Group by..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="input">Input Type</SelectItem>
                <SelectItem value="category">Category</SelectItem>
                <SelectItem value="keyword">Keyword</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div className="text-lg font-semibold">All Tools</div>
            <div className="text-sm text-muted-foreground">{filtered.length} tools</div>
          </div>
          {Array.from(grouped.entries()).map(([group, tools]) => (
            <div key={group} className="mb-8">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {group}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {tools.map((tool) => (
                  <ToolCard
                    key={tool.id}
                    tool={tool}
                    isFavorite={favoriteToolIds.includes(tool.id)}
                    onOpen={() => handleUseTool(tool.id)}
                    onToggleFavorite={() => toggleFavorite(tool.id)}
                    onAddToPipeline={() => addTool(tool.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      </TooltipProvider>
    </div>
  );
};
